/**
 * Edits collaborative Markdown notes within ordinary Vault authorization.
 *
 * Notes are lightweight inline content, but they still inherit folder access
 * and save state asynchronously. The editor owns draft/autosave presentation
 * while the note service remains the durable authorization boundary.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { MarkdownEditor } from '@/components/ui/markdown-editor/markdown-editor'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import type { ItemResponse } from '../types'

interface VaultNoteEditorProps {
  note: ItemResponse | null
  onClose: () => void
  onSave: (body: string, revision: number) => Promise<ItemResponse>
}

export function VaultNoteEditor({ note, onClose, onSave }: VaultNoteEditorProps) {
  const [body, setBody] = useState('')
  const [status, setStatus] = useState<'saved' | 'saving' | 'error'>('saved')
  const revision = useRef(0)
  const lastSaved = useRef('')
  const latestBody = useRef('')
  const failed = useRef(false)
  const timer = useRef<number>()
  const saveQueue = useRef(Promise.resolve())

  useEffect(() => {
    const content = note?.item.noteBody ?? ''
    window.clearTimeout(timer.current)
    setBody(content)
    revision.current = note?.item.revision ?? 0
    lastSaved.current = content
    latestBody.current = content
    failed.current = false
    saveQueue.current = Promise.resolve()
    setStatus('saved')
    return () => window.clearTimeout(timer.current)
  }, [note?.item.itemId])

  const changeBody = useCallback((nextBody: string) => {
    setBody(nextBody)
    latestBody.current = nextBody
    window.clearTimeout(timer.current)
    if (!note || failed.current || nextBody === lastSaved.current) return

    setStatus('saving')
    timer.current = window.setTimeout(() => {
      // Save in revision order so a fast second edit cannot race the first
      // response and submit the same optimistic-lock revision twice.
      saveQueue.current = saveQueue.current.then(async () => {
        if (failed.current) return
        try {
          const saved = await onSave(nextBody, revision.current)
          revision.current = saved.item.revision
          lastSaved.current = nextBody
          if (latestBody.current === nextBody) setStatus('saved')
        } catch {
          failed.current = true
          setStatus('error')
        }
      })
    }, 700)
  }, [note, onSave])

  return (
    <Dialog open={note !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 pr-8">
            <DialogTitle>{note?.item.name}</DialogTitle>
            <Badge variant={status === 'error' ? 'destructive' : 'secondary'}>{status}</Badge>
          </div>
          <DialogDescription>
            Changes save automatically while this note is open.
          </DialogDescription>
        </DialogHeader>
        <MarkdownEditor
          value={body}
          onChange={changeBody}
          ariaLabel="Vault Note content"
          toolbar="full"
        />
        {status === 'error' && (
          <div className="flex items-center justify-between rounded-md border border-destructive/40 p-3 text-sm">
            <span>Save conflict or connection error. Reload the note before editing further.</span>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
